import React, { useState } from "react";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { Input, Select, TreeSelect } from "antd";
import searchSrc from "../doc/plat/assets/search.svg";

const FILTER_KIND = {
  BRANCH: "branch",
  GROUP: "group",
  CAT: "cat"
};

const KIND_LABEL = {
  [FILTER_KIND.BRANCH]: "Филиал",
  [FILTER_KIND.GROUP]: "Группа",
  [FILTER_KIND.CAT]: "Категория"
};

const MiniSelect = ({ autoFocus, defaultOpen, options, placeholder, value, onChange, width }) => {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <Select
      autoFocus={autoFocus}
      open={open}
      onOpenChange={setOpen}
      showSearch
      optionFilterProp="label"
      placeholder={placeholder}
      value={value}
      options={options}
      popupMatchSelectWidth={false}
      variant="borderless"
      suffixIcon={null}
      style={{ width }}
      onChange={(next) => {
        setOpen(false);
        onChange?.(next);
      }}
    />
  );
};

const GroupTreeSelect = ({ value, treeData, onChange }) => {
  const [open, setOpen] = useState(true);
  return (
    <TreeSelect
      autoFocus
      open={open}
      onOpenChange={setOpen}
      allowClear={false}
      showSearch
      treeDefaultExpandAll
      treeNodeFilterProp="title"
      placeholder="Группа"
      value={value}
      treeData={treeData}
      popupMatchSelectWidth={false}
      variant="borderless"
      suffixIcon={null}
      style={{ width: 200 }}
      onChange={(next) => {
        setOpen(false);
        onChange?.(next);
      }}
    />
  );
};

const findTreeLabel = (nodes, value) => {
  for (const n of nodes || []) {
    if (n.value === value) return n.title;
    const found = findTreeLabel(n.children, value);
    if (found) return found;
  }
  return "";
};

const parseChipKey = (key) => {
  if (typeof key === "string" && key.startsWith("cat:")) {
    return { kind: FILTER_KIND.CAT, catId: Number(key.slice(4)) };
  }
  return { kind: key };
};

/**
 * Поиск и чипы фильтров шахматки: филиал, группа, категории.
 */
const BronSearchFilter = ({
  q,
  idBranch,
  idUpper,
  idNumberCats,
  branches,
  categories,
  groupTreeData,
  onChange
}) => {
  const [adding, setAdding] = useState(false);
  const [draftKind, setDraftKind] = useState(null);
  const [editKey, setEditKey] = useState(null);

  const patch = (next) => onChange?.(next);

  const branchOptions = (branches || []).filter((o) => o.value > 0);
  const branchLabel = branchOptions.find((o) => o.value === idBranch)?.label;
  const hasBranch = (idBranch || 0) > 0;

  const groupLabel = idUpper ? findTreeLabel(groupTreeData, idUpper) : "";
  const hasGroup = Boolean(idUpper);
  const hasGroupTree = (groupTreeData || []).length > 0;

  const cats = Array.isArray(idNumberCats) ? idNumberCats : [];
  const unusedCats = (categories || []).filter((c) => !cats.includes(c.value));

  const chips = [];
  if (hasBranch) {
    chips.push({
      key: FILTER_KIND.BRANCH,
      kindLabel: KIND_LABEL[FILTER_KIND.BRANCH],
      valueLabel: branchLabel || String(idBranch)
    });
  }
  if (hasGroup) {
    chips.push({
      key: FILTER_KIND.GROUP,
      kindLabel: KIND_LABEL[FILTER_KIND.GROUP],
      valueLabel: groupLabel || String(idUpper)
    });
  }
  cats.forEach((id) => {
    chips.push({
      key: `cat:${id}`,
      kindLabel: KIND_LABEL[FILTER_KIND.CAT],
      valueLabel: (categories || []).find((c) => c.value === id)?.label || String(id)
    });
  });

  const availableKinds = [
    branchOptions.length > 0 && !hasBranch
      ? { value: FILTER_KIND.BRANCH, label: KIND_LABEL[FILTER_KIND.BRANCH] }
      : null,
    hasGroupTree && !hasGroup
      ? { value: FILTER_KIND.GROUP, label: KIND_LABEL[FILTER_KIND.GROUP] }
      : null,
    unusedCats.length > 0
      ? { value: FILTER_KIND.CAT, label: KIND_LABEL[FILTER_KIND.CAT] }
      : null
  ].filter(Boolean);
  const canAdd = availableKinds.length > 0;

  const editParsed = editKey ? parseChipKey(editKey) : null;
  const activeKind = editParsed?.kind || draftKind;

  const catValueOptions =
    editParsed?.kind === FILTER_KIND.CAT
      ? (categories || []).filter(
          (c) => c.value === editParsed.catId || unusedCats.some((u) => u.value === c.value)
        )
      : unusedCats;

  const cancelAdd = () => {
    setAdding(false);
    setDraftKind(null);
    setEditKey(null);
  };

  const applyKind = (kind, next, catId) => {
    if (kind === FILTER_KIND.BRANCH) {
      patch({ idBranch: next || 0, idUpper: null });
    } else if (kind === FILTER_KIND.GROUP) {
      patch({ idUpper: next || null });
    } else if (kind === FILTER_KIND.CAT) {
      const value = Number(next) || 0;
      if (!value) return;
      if (catId) {
        patch({
          idNumberCats: cats.map((id) => (id === catId ? value : id)).filter((id, i, arr) => arr.indexOf(id) === i)
        });
      } else if (!cats.includes(value)) {
        patch({ idNumberCats: [...cats, value] });
      }
    }
  };

  const clearChip = (key) => {
    const { kind, catId } = parseChipKey(key);
    if (kind === FILTER_KIND.BRANCH) patch({ idBranch: 0, idUpper: null });
    else if (kind === FILTER_KIND.GROUP) patch({ idUpper: null });
    else if (kind === FILTER_KIND.CAT) patch({ idNumberCats: cats.filter((id) => id !== catId) });
    if (editKey === key) setEditKey(null);
  };

  const startAdd = () => {
    setEditKey(null);
    setDraftKind(availableKinds.length === 1 ? availableKinds[0].value : null);
    setAdding(true);
  };

  const pickDraftKind = (kind) => setDraftKind(kind);

  const pickDraftValue = (next) => {
    if (editParsed) {
      applyKind(editParsed.kind, next, editParsed.catId);
      setEditKey(null);
      return;
    }
    if (draftKind == null) return;
    applyKind(draftKind, next);
    cancelAdd();
  };

  const draftValue =
    editParsed?.kind === FILTER_KIND.BRANCH && hasBranch
      ? idBranch
      : editParsed?.kind === FILTER_KIND.GROUP && hasGroup
        ? idUpper
        : editParsed?.kind === FILTER_KIND.CAT
          ? editParsed.catId
          : undefined;

  const renderValuePicker = () => {
    if (activeKind === FILTER_KIND.GROUP) {
      return (
        <GroupTreeSelect
          value={draftValue}
          treeData={groupTreeData}
          onChange={pickDraftValue}
        />
      );
    }
    const options =
      activeKind === FILTER_KIND.BRANCH
        ? branchOptions
        : catValueOptions;
    return (
      <MiniSelect
        autoFocus
        defaultOpen
        options={options}
        placeholder="Значение"
        value={draftValue}
        width={160}
        onChange={pickDraftValue}
      />
    );
  };

  return (
    <div className="bron-calendar__search" role="group" aria-label="Поиск и фильтр">
      <span className="bron-calendar__search-icon">
        <img src={searchSrc} alt="" />
      </span>
      <Input
        variant="borderless"
        allowClear
        className="bron-calendar__search-input"
        value={q || ""}
        placeholder="Номер, категория, гость..."
        onChange={(e) => patch({ q: e.target.value })}
      />
      {chips.map((chip) =>
        editKey === chip.key ? (
          <span key={chip.key} className="bron-calendar__search-draft">
            <span className="bron-calendar__search-chip-kind">{chip.kindLabel}</span>
            {renderValuePicker()}
            <button
              type="button"
              className="bron-calendar__search-chip-clear"
              title="Закрыть"
              aria-label="Закрыть"
              onClick={() => setEditKey(null)}
            >
              <CloseOutlined />
            </button>
          </span>
        ) : (
          <span key={chip.key} className="bron-calendar__search-chip">
            <button
              type="button"
              className="bron-calendar__search-chip-btn"
              title="Изменить значение"
              onClick={() => {
                setAdding(false);
                setDraftKind(null);
                setEditKey(chip.key);
              }}
            >
              <span className="bron-calendar__search-chip-kind">{chip.kindLabel}</span>
              <span className="bron-calendar__search-chip-value">{chip.valueLabel}</span>
            </button>
            <button
              type="button"
              className="bron-calendar__search-chip-clear"
              title="Сбросить"
              aria-label={`Сбросить: ${chip.kindLabel}`}
              onClick={() => clearChip(chip.key)}
            >
              <CloseOutlined />
            </button>
          </span>
        )
      )}
      {adding ? (
        <span className="bron-calendar__search-draft">
          {draftKind == null ? (
            <MiniSelect
              autoFocus
              defaultOpen
              options={availableKinds}
              placeholder="Фильтр"
              value={draftKind}
              width={150}
              onChange={pickDraftKind}
            />
          ) : (
            <>
              <span className="bron-calendar__search-chip-kind">
                {availableKinds.find((k) => k.value === draftKind)?.label || KIND_LABEL[draftKind]}
              </span>
              {renderValuePicker()}
            </>
          )}
          <button
            type="button"
            className="bron-calendar__search-chip-clear"
            title="Отменить"
            aria-label="Отменить добавление фильтра"
            onClick={cancelAdd}
          >
            <CloseOutlined />
          </button>
        </span>
      ) : null}
      {canAdd && !adding && !editKey ? (
        <button
          type="button"
          className="bron-calendar__search-add"
          title="Добавить фильтр"
          aria-label="Добавить фильтр"
          onClick={startAdd}
        >
          <PlusOutlined />
        </button>
      ) : null}
    </div>
  );
};

export default BronSearchFilter;
